import { button, divider, icon, panel, pips, slot, statRow, tabs } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import type { AbilitiesView, BlessingRow, OathRow, SkillRow } from "./abilities-model.ts";
import { keyHint } from "./settings.ts";

type Tab = "bencaos" | "habilidades";

const OATH_HINT = "A cada amanhecer e a cada chefe derrubado, escolha 1 entre 2 melhorias da sua classe (até 5).";

/**
 * Bênçãos e Habilidades (F70, tecla B; F82 no mockup `eldrun-ui-d.png`): as abas da folha (Bênçãos) e do
 * raio (Habilidades) em cima; à esquerda, a grade de slots (as Bênçãos; ou a passiva, a ativa e a ultimate
 * da classe e os 5 lugares do Juramento da Alvorada, com a oferta aberta); à direita, o detalhe do slot
 * escolhido (ícone, nome, pips, descrição e números). Escolher uma opção da oferta envia ao servidor, que
 * confere. Redesenha só quando a projeção, a aba ou a escolha mudam.
 */
export class AbilitiesPanel {
  readonly element: HTMLElement;
  private readonly tabBar: { element: HTMLElement; select(id: string): void };
  private readonly left: HTMLElement;
  private readonly detail: HTMLElement;
  private tab: Tab = "habilidades";
  private picks: Record<Tab, string | undefined> = { bencaos: undefined, habilidades: undefined };
  private view: AbilitiesView | undefined;
  private key = "";
  open = false;

  constructor(parent: HTMLElement, choose: (oath: string) => void) {
    this.tabBar = tabs(
      [
        { id: "bencaos", label: "Bênçãos", icon: "aba-bencaos" },
        { id: "habilidades", label: "Habilidades", icon: "aba-habilidades" },
      ],
      (id) => this.select(id as Tab),
      this.tab,
    );
    this.tabBar.element.classList.add("hab-abas");
    this.tabBar.element.querySelectorAll<HTMLElement>("[role=tab]").forEach((b) => (b.dataset["tab"] = b.dataset["aba"] ?? ""));
    this.left = h("div", { class: "hab-grades" });
    this.detail = h("div", { class: "hab-detalhe-corpo", "aria-live": "polite" });
    this.element = h(
      "section",
      { id: "bencaos-habilidades", class: "hab-painel", "aria-label": "Bênçãos e Habilidades" },
      h("div", { class: "hab-esquerda" }, this.tabBar.element, this.left),
      panel({ title: "Detalhe", icon: "icone-detalhe", className: "hab-detalhe", onClose: () => this.toggle(false), closeLabel: "Fechar Bênçãos e Habilidades" }, this.detail, keyHint("abilities", "{} fecha")),
    );
    this.element.hidden = true;
    this.element.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const offer = target.closest<HTMLElement>("button[data-oath]");
      if (offer?.dataset["oath"]) {
        choose(offer.dataset["oath"]);
        return;
      }
      const pick = target.closest<HTMLElement>("[data-escolha]");
      if (pick?.dataset["escolha"]) this.pick(pick.dataset["escolha"]);
    });
    this.element.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.toggle(false);
    });
    parent.appendChild(this.element);
  }

  toggle(force?: boolean): void {
    this.open = force ?? !this.open;
    this.element.hidden = !this.open;
  }

  /** Abre direto na aba do Juramento (a oferta chegou). */
  select(tab: Tab): void {
    this.tab = tab;
    this.tabBar.select(tab);
    this.key = "";
    if (this.view) this.update(this.view);
  }

  update(view: AbilitiesView): void {
    this.view = view;
    const ids = this.ids(view, this.tab);
    // A escolha fica enquanto o slot existe; senão, o primeiro da aba (a oferta aberta, se houver, na das Habilidades).
    let picked = this.picks[this.tab];
    if (picked === undefined || !ids.includes(picked)) picked = this.tab === "habilidades" && view.offer[0] ? `oferta:${view.offer[0].id}` : ids[0];
    this.picks[this.tab] = picked;
    const key = `${this.tab}#${picked}#${JSON.stringify(view)}`;
    if (key === this.key) return;
    this.key = key;
    this.left.replaceChildren(...(this.tab === "bencaos" ? this.blessings(view, picked) : this.skills(view, picked)));
    this.detail.replaceChildren(...this.detailFor(view, picked ?? ""));
  }

  private pick(id: string): void {
    this.picks[this.tab] = id;
    if (this.view) this.update(this.view);
  }

  /** As escolhas possíveis da aba, na ordem da grade. */
  private ids(view: AbilitiesView, tab: Tab): string[] {
    if (tab === "bencaos") return view.blessings.map((b) => `bencao:${b.id}`);
    return [...view.skills.map((s) => `habilidade:${s.id}`), ...view.oaths.map((_, i) => `juramento:${i}`), ...view.offer.map((o) => `oferta:${o.id}`)];
  }

  private blessings(view: AbilitiesView, picked: string | undefined): HTMLElement[] {
    const cells = view.blessings.map((b) => {
      const el = slot({ label: `${b.name}, ${b.rarity}${b.stacks > 1 ? `, ×${b.stacks}` : ""}`, icon: "aba-bencaos", tier: b.tier, qty: b.stacks, selected: picked === `bencao:${b.id}` });
      el.dataset["bencao"] = b.id;
      el.dataset["escolha"] = `bencao:${b.id}`;
      el.classList.add("bencao");
      return el;
    });
    // A grade cheia como no mockup (5 por linha, pelo menos 2 linhas); os lugares a mais ficam apagados.
    const total = Math.max(10, Math.ceil(view.blessings.length / 5) * 5);
    for (let i = cells.length; i < total; i++) {
      const el = slot({ label: "vazio" });
      el.disabled = true;
      el.classList.add("livre");
      cells.push(el);
    }
    return [
      panel(
        { title: "Bênçãos", icon: "aba-bencaos", className: "k-painel--sub hab-grupo" },
        h("div", { class: "hab-grade" }, ...cells),
        view.blessings.length === 0 ? h("p", { class: "hint hab-dica" }, "Nenhuma Bênção ainda. Os santuários e os baús das Bênçãos dão as primeiras.") : h("p", { class: "hint hab-dica" }, `${view.blessings.length} ${view.blessings.length === 1 ? "Bênção" : "Bênçãos"} · ◆ comum, ◆◆ rara, ◆◆◆ lendária`),
      ),
    ];
  }

  private skills(view: AbilitiesView, picked: string | undefined): HTMLElement[] {
    const skillCell = (s: SkillRow) => {
      const el = slot({ label: `${s.kind}: ${s.name}${s.key ? ` (${s.key})` : ""}`, content: h("span", { class: "hab-glifo", "aria-hidden": "true" }, view.classGlyph), tier: s.level, selected: picked === `habilidade:${s.id}` });
      el.dataset["skill"] = s.id;
      el.dataset["escolha"] = `habilidade:${s.id}`;
      el.classList.add("habilidade");
      return h("div", { class: "hab-celula" }, el, h("span", { class: "hab-legenda" }, s.kind));
    };
    const oathCell = (o: OathRow | undefined, i: number) => {
      const el = slot({ label: o ? `Juramento ${i + 1}: ${o.name}` : `Juramento ${i + 1}: vazio`, content: h("span", { class: "hab-glifo", "aria-hidden": "true" }, o ? "◆" : "◇"), selected: picked === `juramento:${i}` });
      el.dataset["escolha"] = `juramento:${i}`;
      el.classList.add("juramento", o ? "feito" : "vazio");
      if (o) el.title = o.description;
      return h("div", { class: "hab-celula" }, el, h("span", { class: "hab-legenda" }, o ? o.name : `${i + 1}`));
    };
    return [
      panel({ title: `Habilidades · ${view.className}`, icon: "aba-habilidades", className: "k-painel--sub hab-grupo" }, h("div", { class: "hab-grade hab-grade--classe" }, ...view.skills.map(skillCell))),
      panel(
        { title: "Juramento da Alvorada", icon: "icone-talentos", className: "k-painel--sub hab-grupo" },
        h("div", { class: "hab-grade juramentos" }, ...view.oaths.map(oathCell)),
        view.offer.length > 0
          ? h(
              "div",
              { class: "juramento-oferta" },
              h("span", { class: "hab-oferta-titulo" }, "Escolha uma melhoria"),
              ...view.offer.map((o) =>
                h(
                  "button",
                  // Escolher só seleciona (revisão da F82): o detalhe mostra e o "Jurar" de lá é que jura.
                  { type: "button", class: `hab-oferta${picked === `oferta:${o.id}` ? " ativo" : ""}`, "data-escolha": `oferta:${o.id}`, title: o.description },
                  h("strong", {}, o.name),
                  h("span", {}, o.description),
                ),
              ),
            )
          : h("p", { class: "hint hab-dica" }, OATH_HINT),
      ),
    ];
  }

  /** O detalhe da escolha (`bencao:`, `habilidade:`, `juramento:` ou `oferta:` e o id). */
  private detailFor(view: AbilitiesView, picked: string): HTMLElement[] {
    const split = picked.indexOf(":");
    const kind = picked.slice(0, split);
    const id = picked.slice(split + 1);
    if (kind === "bencao") {
      const b = view.blessings.find((x) => x.id === id);
      if (b) return this.blessingDetail(b);
    }
    if (kind === "habilidade") {
      const s = view.skills.find((x) => x.id === id);
      if (s) return this.card(view.classGlyph, s.name, `${s.kind}${s.key ? ` · ${s.key}` : ""}`, s.levels > 0 ? pips(s.level, s.levels) : undefined, s.description, s.stats.map((r) => this.stat(r.label, r.value, r.boosted)));
    }
    if (kind === "juramento") {
      const o = view.oaths[Number(id)];
      if (o) return this.card("◆", o.name, `Juramento ${Number(id) + 1}${o.skill ? ` · ${o.skill}` : ""}`, undefined, o.description, []);
      return this.card("◇", "Lugar vazio", `Juramento ${Number(id) + 1} de ${view.oaths.length}`, undefined, OATH_HINT, []);
    }
    if (kind === "oferta") {
      const o = view.offer.find((x) => x.id === id);
      if (o) return [...this.card("◆", o.name, `Oferta${o.skill ? ` · ${o.skill}` : ""}`, undefined, o.description, []), h("div", { class: "hab-acoes" }, this.oathButton(o))];
    }
    return [h("p", { class: "hint hab-dica" }, this.tab === "bencaos" ? "Nenhuma Bênção ainda." : "Escolha um slot.")];
  }

  private blessingDetail(b: BlessingRow): HTMLElement[] {
    return this.card("", b.name, `Bênção ${b.rarity}${b.stacks > 1 ? ` · ×${b.stacks}` : ""}`, pips(b.tier, 3), b.description, [this.stat("Raridade", b.rarity), this.stat("Pilhas", String(b.stacks)), this.stat("Com mais uma", b.next)], true);
  }

  /** Botão de jurar no detalhe da oferta (o único com `data-oath`: escolher na grade não jura). */
  private oathButton(o: OathRow): HTMLButtonElement {
    const el = button({ label: `Jurar: ${o.name}`, icon: "icone-confirmar", variant: "primario" });
    el.dataset["oath"] = o.id;
    return el;
  }

  private stat(label: string, value: string, boosted = false): HTMLElement {
    const row = statRow(undefined, label, value);
    if (boosted) {
      row.classList.add("realce");
      row.title = "Melhorado por um juramento";
    }
    return row;
  }

  /** Topo (moldura com o ícone, nome, tipo e pips), descrição e linhas de número, como o detalhe do mockup. */
  private card(glyph: string, name: string, kind: string, level: HTMLElement | undefined, text: string, rows: HTMLElement[], leaf = false): HTMLElement[] {
    return [
      h(
        "div",
        { class: "hab-detalhe-topo" },
        h("span", { class: "hab-detalhe-moldura" }, leaf ? icon("aba-bencaos", "hab-detalhe-icone") : h("span", { class: "hab-detalhe-glifo", "aria-hidden": "true" }, glyph)),
        h("div", { class: "hab-detalhe-titulos" }, h("h3", { class: "hab-detalhe-nome" }, name), h("span", { class: "hab-detalhe-tipo" }, kind), level),
      ),
      divider(),
      h("p", { class: "hab-detalhe-texto" }, text),
      ...(rows.length > 0 ? [h("div", { class: "hab-detalhe-linhas" }, ...rows)] : []),
    ];
  }

  remove(): void {
    this.element.remove();
  }
}
